/**
 * Personal Mode Limits for Schema Compare
 *
 * Defines the object count limits for Personal mode and provides
 * utility functions for checking if schemas exceed these limits.
 */

/**
 * Maximum total object count for full schema comparison in Personal mode.
 * Schemas larger than this require Pro mode.
 */
export const PERSONAL_MODE_MAX_OBJECTS = 200;

/**
 * Object counts for a database schema
 */
export type SchemaObjectCounts = {
  tables: number;
  views: number;
  procedures: number;
  functions: number;
  /** Sequences (SQL Server / PostgreSQL / Oracle). Absent on older payloads. */
  sequences?: number;
  /** SQL Server user-defined types (S7). Absent on older payloads and other engines. */
  userTypes?: number;
};

/**
 * Calculate total object count from counts object.
 *
 * Sequences are part of the total because the compare diffs them. They are
 * `?? 0` rather than required so a payload from a build that predates
 * sequence counting still totals correctly instead of yielding NaN.
 */
export function getTotalObjectCount(counts: SchemaObjectCounts): number {
  return (
    counts.tables +
    counts.views +
    counts.procedures +
    counts.functions +
    (counts.sequences ?? 0) +
    (counts.userTypes ?? 0)
  );
}

/**
 * Check result for Personal mode schema compare limits
 */
export type PersonalModeLimitCheck = {
  /** Whether the comparison exceeds Personal mode limits */
  exceedsLimit: boolean;
  /** Source schema object count (null if unknown) */
  sourceObjectCount: number | null;
  /** Target schema object count (null if unknown) */
  targetObjectCount: number | null;
  /** Which schema(s) exceed the limit */
  limitExceededBy: 'source' | 'target' | 'both' | 'none';
  /** Human-readable description of the limit status */
  description: string;
};

/**
 * Checks if a schema comparison exceeds Personal mode limits.
 *
 * In Personal mode, Schema Compare is only allowed when both schemas
 * have <= 200 total objects (tables + views + procedures + functions).
 * If either schema exceeds this limit, the user must upgrade to Pro.
 *
 * @param sourceObjectCount - Total object count for source schema (null if unknown)
 * @param targetObjectCount - Total object count for target schema (null if unknown)
 * @returns Limit check result
 */
export function checkPersonalModeLimits(
  sourceObjectCount: number | null,
  targetObjectCount: number | null
): PersonalModeLimitCheck {
  // If either count is null/unknown, assume it's within limits
  // (we can't block without knowing the count)
  const sourceExceeds = sourceObjectCount !== null && sourceObjectCount > PERSONAL_MODE_MAX_OBJECTS;
  const targetExceeds = targetObjectCount !== null && targetObjectCount > PERSONAL_MODE_MAX_OBJECTS;
  const exceedsLimit = sourceExceeds || targetExceeds;

  let limitExceededBy: 'source' | 'target' | 'both' | 'none' = 'none';
  if (sourceExceeds && targetExceeds) {
    limitExceededBy = 'both';
  } else if (sourceExceeds) {
    limitExceededBy = 'source';
  } else if (targetExceeds) {
    limitExceededBy = 'target';
  }

  const description = getPersonalModeLimitDescription(
    sourceObjectCount,
    targetObjectCount,
    limitExceededBy
  );

  return {
    exceedsLimit,
    sourceObjectCount,
    targetObjectCount,
    limitExceededBy,
    description,
  };
}

/**
 * Generates a human-readable description of the limit status.
 */
function getPersonalModeLimitDescription(
  sourceObjectCount: number | null,
  targetObjectCount: number | null,
  limitExceededBy: 'source' | 'target' | 'both' | 'none'
): string {
  const formatCount = (count: number | null) =>
    count !== null ? count.toLocaleString() : 'unknown';

  switch (limitExceededBy) {
    case 'both':
      return `Both schemas exceed the ${PERSONAL_MODE_MAX_OBJECTS.toLocaleString()} object limit for Personal mode. ` +
        `Source has ${formatCount(sourceObjectCount)} objects, target has ${formatCount(targetObjectCount)} objects.`;
    case 'source':
      return `Source schema exceeds the ${PERSONAL_MODE_MAX_OBJECTS.toLocaleString()} object limit for Personal mode ` +
        `(${formatCount(sourceObjectCount)} objects).`;
    case 'target':
      return `Target schema exceeds the ${PERSONAL_MODE_MAX_OBJECTS.toLocaleString()} object limit for Personal mode ` +
        `(${formatCount(targetObjectCount)} objects).`;
    case 'none':
    default:
      return `Schemas are within Personal mode limits (${formatCount(sourceObjectCount)} and ${formatCount(targetObjectCount)} objects).`;
  }
}

/**
 * Formats the object count limits for display in the UI.
 *
 * @returns Human-readable string describing the Personal mode limit
 */
export function getPersonalModeLimitString(): string {
  return `${PERSONAL_MODE_MAX_OBJECTS.toLocaleString()} objects`;
}
